import React, { useState, useEffect } from 'react';
import {
  Page,
  Card,
  Text,
  Button,
  Banner,
  BlockStack,
  InlineStack,
  Badge,
  Spinner,
  Layout,
  Modal,
  TextField,
  Select,
  FormLayout
} from '@shopify/polaris';
import { useAuth } from '../contexts/AuthContext';
import { useClient } from '../contexts/ClientContext';
import { API_CONFIG } from '../config/api';
import axios from 'axios';

interface BillingInfo {
  current_plan: {
    id: number;
    plan_name: string;
    status: string;
    start_date: string;
    end_date: string;
    available_credits: number;
    monthly_cost: number;
    cost_per_minute: number;
  } | null;
  has_active_plan: boolean;
  can_use_ai_service: boolean;
  usage_summary: {
    total_minutes: number;
    total_cost: number;
    credits_added: number;
    credits_deducted: number;
    payments_received: number;
  };
}

interface PricingPlan {
  id: number;
  name: string;
  monthly_cost: number;
  credits_per_month: number;
  cost_per_minute: number;
  description: string;
}

const Billing: React.FC = () => {
  const { user } = useAuth();
  const { selectedClient } = useClient();
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal states
  const [showAddCreditsModal, setShowAddCreditsModal] = useState(false);
  const [showSwitchPlanModal, setShowSwitchPlanModal] = useState(false);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  const [showEditPricingModal, setShowEditPricingModal] = useState(false);
  
  // Form states
  const [creditsAmount, setCreditsAmount] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  
  // Edit pricing form states
  const [baseCost, setBaseCost] = useState('');
  const [costPerMinute, setCostPerMinute] = useState('');
  const [pricingDescription, setPricingDescription] = useState('');
  
  // Payment history states
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
  const [paymentHistoryError, setPaymentHistoryError] = useState<string | null>(null);
  const [paymentHistoryPagination, setPaymentHistoryPagination] = useState<any>(null);

  useEffect(() => {
    if (selectedClient) {
      loadBillingInfo();
      loadPricingPlans();
    }
  }, [selectedClient]);

  // Populate edit pricing form when modal opens
  useEffect(() => {
    if (showEditPricingModal && billingInfo?.current_plan) {
      setBaseCost(billingInfo.current_plan.monthly_cost.toString());
      setCostPerMinute(billingInfo.current_plan.cost_per_minute.toString());
      setPricingDescription('');
    }
  }, [showEditPricingModal, billingInfo]);

  const loadBillingInfo = async () => {
    if (!selectedClient) return;

    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_CONFIG.FULL_BASE_URL}/support_clients/${selectedClient.id}/billing`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setBillingInfo(response.data.data);
      } else {
        setError('Failed to load billing information');
      }
    } catch (err: any) {
      console.error('Error loading billing info:', err);
      setError(err.response?.data?.error || 'Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const loadPricingPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API_CONFIG.FULL_BASE_URL}/pricing_plans`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setPricingPlans(response.data.data.plans || []);
      }
    } catch (err: any) {
      console.error('Error loading pricing plans:', err);
    }
  };

  const handleAddCredits = async () => {
    if (!creditsAmount || !selectedClient) return;

    try {
      setActionLoading(true);
      setActionError(null);

      const response = await axios.post(`${API_CONFIG.FULL_BASE_URL}/support_clients/${selectedClient.id}/add_credits`, {
        amount: parseFloat(creditsAmount)
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setActionSuccess('Credits added successfully!');
        setShowAddCreditsModal(false);
        setCreditsAmount('');
        // Reload billing info to reflect changes
        await loadBillingInfo();
      } else {
        setActionError(response.data.error || 'Failed to add credits');
      }
    } catch (err: any) {
      console.error('Error adding credits:', err);
      setActionError(err.response?.data?.error || 'Failed to add credits');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSwitchPlan = async () => {
    if (!selectedPlan || !selectedClient) return;

    try {
      setActionLoading(true);
      setActionError(null);

      const response = await axios.post(`${API_CONFIG.FULL_BASE_URL}/support_clients/${selectedClient.id}/assign_plan`, {
        pricing_plan_id: parseInt(selectedPlan)
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setActionSuccess('Plan switched successfully!');
        setShowSwitchPlanModal(false);
        setSelectedPlan('');
        // Reload billing info to reflect changes
        await loadBillingInfo();
      } else {
        setActionError(response.data.error || 'Failed to switch plan');
      }
    } catch (err: any) {
      console.error('Error switching plan:', err);
      setActionError(err.response?.data?.error || 'Failed to switch plan');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditPricing = async () => {
    if (!baseCost || !costPerMinute || !selectedClient) return;

    try {
      setActionLoading(true);
      setActionError(null);

      const response = await axios.patch(`${API_CONFIG.FULL_BASE_URL}/support_clients/${selectedClient.id}/edit_plan_pricing`, {
        base_cost: parseFloat(baseCost),
        cost_per_minute: parseFloat(costPerMinute),
        description: pricingDescription || undefined
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setActionSuccess('Plan pricing updated successfully!');
        setShowEditPricingModal(false);
        setBaseCost('');
        setCostPerMinute('');
        setPricingDescription('');
        // Reload billing info to reflect changes
        await loadBillingInfo();
      } else {
        setActionError(response.data.error || 'Failed to update plan pricing');
      }
    } catch (err: any) {
      console.error('Error updating plan pricing:', err);
      setActionError(err.response?.data?.error || 'Failed to update plan pricing');
    } finally {
      setActionLoading(false);
    }
  };

  const loadPaymentHistory = async (page = 1) => {
    if (!selectedClient) return;

    try {
      setPaymentHistoryLoading(true);
      setPaymentHistoryError(null);

      const response = await axios.get(`${API_CONFIG.FULL_BASE_URL}/support_clients/${selectedClient.id}/payment_history`, {
        params: {
          page: page,
          per_page: 20
        },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setPaymentHistory(response.data.data.payments);
        setPaymentHistoryPagination(response.data.data.pagination);
      } else {
        setPaymentHistoryError('Failed to load payment history');
      }
    } catch (err: any) {
      console.error('Error loading payment history:', err);
      setPaymentHistoryError(err.response?.data?.error || 'Failed to load payment history');
    } finally {
      setPaymentHistoryLoading(false);
    }
  };

  const handleViewPaymentHistory = async () => {
    setShowPaymentHistoryModal(true);
    await loadPaymentHistory();
  };

  if (user?.role !== 'support_manager' && user?.role !== 'admin') {
    return (
      <Page>
        <Banner tone="critical">
          <p>Access denied. Only support managers and admins can view billing information.</p>
        </Banner>
      </Page>
    );
  }

  if (!selectedClient && user?.role === 'support_manager') {
    return (
      <Page>
        <Banner tone="info">
          <p>Please select a client to view their billing information.</p>
        </Banner>
      </Page>
    );
  }

  // For admin users, we'll need to implement a different approach
  // For now, show a message that admin billing is not yet implemented
  if (user?.role === 'admin' && !selectedClient) {
    return (
      <Page>
        <Banner tone="info">
          <p>Admin billing overview is not yet implemented. Please select a client to view their billing information.</p>
        </Banner>
      </Page>
    );
  }

  if (loading) {
    return (
      <Page title="Billing" fullWidth>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spinner size="large" />
          <Text as="p" tone="subdued">Loading billing information...</Text>
        </div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Billing" fullWidth>
        <Banner tone="critical">
          <p>Error: {error}</p>
          <Button onClick={loadBillingInfo} variant="primary">
            Retry
          </Button>
        </Banner>
      </Page>
    );
  }

  return (
    <Page title="Billing" fullWidth>
      {actionSuccess && (
        <div style={{ marginBottom: '16px' }}>
          <Banner tone="success" onDismiss={() => setActionSuccess(null)}>
            <p>{actionSuccess}</p>
          </Banner>
        </div>
      )}

      <Layout>
        <Layout.Section>
          <BlockStack gap="500">
            {/* Current Plan */}
            <Card>
              <div style={{ padding: '16px' }}>
                <Text variant="headingMd" as="h2">
                  Current Plan
                </Text>
                {billingInfo?.current_plan ? (
                  <BlockStack gap="400">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Text variant="bodyMd" as="p" fontWeight="semibold">
                        {billingInfo.current_plan.plan_name}
                      </Text>
                      <Badge tone={billingInfo.current_plan.status === 'active' ? 'success' : 'warning'}>
                        {billingInfo.current_plan.status}
                      </Badge>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <Text variant="bodySm" as="p" tone="subdued">Monthly Cost</Text>
                        <Text variant="bodyMd" as="p" fontWeight="semibold">
                          ₹{Number(billingInfo.current_plan.monthly_cost).toFixed(2)}
                        </Text>
                      </div>
                      <div>
                        <Text variant="bodySm" as="p" tone="subdued">Available Credits</Text>
                        <Text variant="bodyMd" as="p" fontWeight="semibold">
                          {billingInfo.current_plan.available_credits}
                        </Text>
                      </div>
                      <div>
                        <Text variant="bodySm" as="p" tone="subdued">Cost per Minute</Text>
                        <Text variant="bodyMd" as="p" fontWeight="semibold">
                          ₹{Number(billingInfo.current_plan.cost_per_minute).toFixed(2)}
                        </Text>
                      </div>
                      <div>
                        <Text variant="bodySm" as="p" tone="subdued">Plan Period</Text>
                        <Text variant="bodyMd" as="p" fontWeight="semibold">
                          {(() => {
                            const startDate = billingInfo.current_plan.start_date ? new Date(billingInfo.current_plan.start_date) : null;
                            const endDate = billingInfo.current_plan.end_date ? new Date(billingInfo.current_plan.end_date) : null;
                            
                            if (!startDate || isNaN(startDate.getTime()) || !endDate || isNaN(endDate.getTime())) {
                              return 'Not specified';
                            }
                            
                            return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
                          })()}
                        </Text>
                      </div>
                    </div>
                  </BlockStack>
                ) : (
                  <Banner tone="warning">
                    <p>No active plan found for this client.</p>
                  </Banner>
                )}
              </div>
            </Card>

            {/* Usage Summary */}
            <Card>
              <div style={{ padding: '16px' }}>
                <Text variant="headingMd" as="h2">
                  Usage Summary
                </Text>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginTop: '16px' }}>
                  <div style={{ padding: '16px', backgroundColor: '#f6f6f7', borderRadius: '8px' }}>
                    <Text variant="bodySm" as="p" tone="subdued">Total Minutes</Text>
                    <Text variant="headingMd" as="p" fontWeight="semibold">
                      {billingInfo?.usage_summary?.total_minutes || 0}
                    </Text>
                  </div>
                  <div style={{ padding: '16px', backgroundColor: '#f6f6f7', borderRadius: '8px' }}>
                    <Text variant="bodySm" as="p" tone="subdued">Total Cost</Text>
                    <Text variant="headingMd" as="p" fontWeight="semibold">
                      ₹{billingInfo?.usage_summary?.total_cost || 0}
                    </Text>
                  </div>
                  <div style={{ padding: '16px', backgroundColor: '#f6f6f7', borderRadius: '8px' }}>
                    <Text variant="bodySm" as="p" tone="subdued">Credits Added</Text>
                    <Text variant="headingMd" as="p" fontWeight="semibold">
                      {billingInfo?.usage_summary?.credits_added || 0}
                    </Text>
                  </div>
                  <div style={{ padding: '16px', backgroundColor: '#f6f6f7', borderRadius: '8px' }}>
                    <Text variant="bodySm" as="p" tone="subdued">Credits Used</Text>
                    <Text variant="headingMd" as="p" fontWeight="semibold">
                      {billingInfo?.usage_summary?.credits_deducted || 0}
                    </Text>
                  </div>
                </div>
              </div>
            </Card>

            {/* Actions */}
            <Card>
              <div style={{ padding: '16px' }}>
                <Text variant="headingMd" as="h2">
                  Billing Actions
                </Text>
                <div style={{ marginTop: '16px' }}>
                  <InlineStack gap="200">
                    <Button 
                      variant="primary"
                      onClick={() => setShowAddCreditsModal(true)}
                      disabled={!billingInfo?.has_active_plan}
                    >
                      Add Credits
                    </Button>
                    <Button 
                      variant="secondary"
                      onClick={handleViewPaymentHistory}
                    >
                      View Payment History
                    </Button>
                    <Button 
                      variant="secondary"
                      onClick={() => setShowSwitchPlanModal(true)}
                    >
                      {billingInfo?.current_plan ? 'Change Plan' : 'Assign Plan'}
                    </Button>
                    <Button 
                      variant="secondary"
                      onClick={() => setShowEditPricingModal(true)}
                      disabled={!billingInfo?.has_active_plan}
                    >
                      Edit Plan Pricing
                    </Button>
                  </InlineStack>
                  {!billingInfo?.has_active_plan && (
                    <div style={{ marginTop: '12px' }}>
                      <Text variant="bodySm" as="p" tone="subdued">
                        Please assign a plan first before adding credits.
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>

      {/* Add Credits Modal */}
      <Modal
        open={showAddCreditsModal}
        onClose={() => setShowAddCreditsModal(false)}
        title="Add Credits"
        primaryAction={{
          content: 'Add Credits',
          onAction: handleAddCredits,
          loading: actionLoading
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowAddCreditsModal(false)
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {actionError && (
              <Banner tone="critical">
                <p>{actionError}</p>
              </Banner>
            )}
            <FormLayout>
              <TextField
                label="Number of Credits"
                type="number"
                value={creditsAmount}
                onChange={setCreditsAmount}
                placeholder="Enter number of credits to add"
                min="1"
                autoComplete="off"
              />
            </FormLayout>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Switch Plan Modal */}
      <Modal
        open={showSwitchPlanModal}
        onClose={() => setShowSwitchPlanModal(false)}
        title="Change Plan"
        primaryAction={{
          content: 'Switch Plan',
          onAction: handleSwitchPlan,
          loading: actionLoading
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowSwitchPlanModal(false)
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {actionError && (
              <Banner tone="critical">
                <p>{actionError}</p>
              </Banner>
            )}
            <FormLayout>
              <Select
                label="Select New Plan"
                options={pricingPlans.map(plan => ({
                  label: `${plan.name} - ₹${Number(plan.monthly_cost).toFixed(2)}/month (0 credits)`,
                  value: plan.id.toString()
                }))}
                value={selectedPlan}
                onChange={setSelectedPlan}
                placeholder="Choose a plan"
              />
              {selectedPlan && (
                <div style={{ padding: '12px', backgroundColor: '#f6f6f7', borderRadius: '6px' }}>
                  <Text variant="bodySm" as="p" tone="subdued">
                    {pricingPlans.find(p => p.id.toString() === selectedPlan)?.description}
                  </Text>
                </div>
              )}
            </FormLayout>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Payment History Modal */}
      <Modal
        open={showPaymentHistoryModal}
        onClose={() => setShowPaymentHistoryModal(false)}
        title="Payment History"
        primaryAction={{
          content: 'Close',
          onAction: () => setShowPaymentHistoryModal(false)
        }}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {paymentHistoryError && (
              <Banner tone="critical">
                <p>{paymentHistoryError}</p>
              </Banner>
            )}
            
            {paymentHistoryLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Spinner size="large" />
                <Text variant="bodyMd" as="p">Loading payment history...</Text>
              </div>
            ) : paymentHistory.length === 0 ? (
              <Banner tone="info">
                <p>No payment history found for this client.</p>
              </Banner>
            ) : (
              <BlockStack gap="400">
                {/* Payment History Table */}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e1e3e5' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>
                          <Text variant="bodyMd" as="p">Date</Text>
                        </th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>
                          <Text variant="bodyMd" as="p">Type</Text>
                        </th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>
                          <Text variant="bodyMd" as="p">Amount</Text>
                        </th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>
                          <Text variant="bodyMd" as="p">Description</Text>
                        </th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>
                          <Text variant="bodyMd" as="p">Support Manager</Text>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentHistory.map((payment) => (
                        <tr key={payment.id} style={{ borderBottom: '1px solid #f1f2f3' }}>
                          <td style={{ padding: '12px' }}>
                            <Text variant="bodyMd" as="p">
                              {new Date(payment.payment_date).toLocaleDateString()}
                            </Text>
                          </td>
                          <td style={{ padding: '12px' }}>
                            <Badge tone={
                              payment.payment_type === 'credit_purchase' ? 'success' :
                              payment.payment_type === 'plan_renewal' ? 'info' :
                              payment.payment_type === 'refund' ? 'warning' :
                              'attention'
                            }>
                              {payment.payment_type.replace('_', ' ').toUpperCase()}
                            </Badge>
                          </td>
                          <td style={{ padding: '12px' }}>
                            <Text variant="bodyMd" as="p" fontWeight="semibold">
                              ₹{Number(payment.amount).toFixed(2)}
                            </Text>
                          </td>
                          <td style={{ padding: '12px' }}>
                            <Text variant="bodyMd" as="p">
                              {payment.description}
                            </Text>
                          </td>
                          <td style={{ padding: '12px' }}>
                            <Text variant="bodyMd" as="p" tone="subdued">
                              {payment.support_manager.email}
                            </Text>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {paymentHistoryPagination && paymentHistoryPagination.total_pages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                    <InlineStack gap="200">
                      <Button
                        variant="secondary"
                        size="slim"
                        disabled={paymentHistoryPagination.current_page === 1}
                        onClick={() => loadPaymentHistory(paymentHistoryPagination.current_page - 1)}
                      >
                        Previous
                      </Button>
                      <Text variant="bodyMd" as="p">
                        Page {paymentHistoryPagination.current_page} of {paymentHistoryPagination.total_pages}
                      </Text>
                      <Button
                        variant="secondary"
                        size="slim"
                        disabled={paymentHistoryPagination.current_page === paymentHistoryPagination.total_pages}
                        onClick={() => loadPaymentHistory(paymentHistoryPagination.current_page + 1)}
                      >
                        Next
                      </Button>
                    </InlineStack>
                  </div>
                )}

                {/* Summary */}
                {paymentHistoryPagination && (
                  <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f6f6f7', borderRadius: '8px' }}>
                    <Text variant="bodyMd" as="p" fontWeight="semibold">
                      Total Payments: ₹{paymentHistoryPagination.total_count > 0 ? 
                        paymentHistory.reduce((sum, payment) => sum + Number(payment.amount), 0).toFixed(2) : 
                        '0.00'
                      }
                    </Text>
                  </div>
                )}
              </BlockStack>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Edit Plan Pricing Modal */}
      <Modal
        open={showEditPricingModal}
        onClose={() => setShowEditPricingModal(false)}
        title="Edit Plan Pricing"
        primaryAction={{
          content: 'Update Pricing',
          onAction: handleEditPricing,
          loading: actionLoading
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowEditPricingModal(false)
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {actionError && (
              <Banner tone="critical">
                <p>{actionError}</p>
              </Banner>
            )}
            <FormLayout>
              <TextField
                label="Base Cost (₹)"
                type="number"
                value={baseCost}
                onChange={setBaseCost}
                placeholder="Enter monthly base cost"
                min="0"
                step={0.01}
                autoComplete="off"
                helpText="Monthly subscription cost in INR"
              />
              <TextField
                label="Cost per Minute (₹)"
                type="number"
                value={costPerMinute}
                onChange={setCostPerMinute}
                placeholder="Enter cost per minute"
                min="0"
                step={0.01}
                autoComplete="off"
                helpText="Cost charged per minute of AI transcription usage"
              />
              <TextField
                label="Description (Optional)"
                value={pricingDescription}
                onChange={setPricingDescription}
                placeholder="Enter reason for pricing change"
                multiline={2}
                autoComplete="off"
                helpText="Optional description for audit purposes"
              />
            </FormLayout>
            <Banner tone="info">
              <p>
                <strong>Note:</strong> This will update the pricing for the current plan. 
                The changes will be applied immediately and logged for audit purposes.
              </p>
            </Banner>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
};

export default Billing;
